import * as React from 'react';
import { Outlet } from 'react-router';
import { gql, useQuery } from '@apollo/client';
import { Patient } from '@quality24/inpatient-typings';
import classNames from 'classnames';

import BottomNavbar from '@/inpatient-patient-pwa/organisms/BottomNavbar';
import Topbar from '@/inpatient-patient-pwa/organisms/Topbar';
import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';

import styles from './NavbarTemplate.module.scss';

export const SELECT_QUERY = gql`
  query FetchTemplateData($admissionId: UUID!) {
    admission: admissionById(id: $admissionId) {
      id
      patient {
        name
      }
    }
  }
`;

export interface QueryResult {
  admission: {
    patient: Pick<Patient, 'name'>;
  };
}

export interface Props {
  className?: string;
  /**
   * Rendered inside `<main>`. When omitted, the route `<Outlet />` is rendered
   * instead — this is the usual path, since the template is mounted as a layout
   * route in `PrivateRouter`. The explicit `children` form is kept for Storybook
   * decorators that wrap a page preview directly.
   */
  children?: React.ReactNode;
  enableTopbar?: boolean;
  enableBottombar?: boolean;
  renderGreeting?: boolean;
}

const NavbarTemplate: React.FunctionComponent<Props> = ({
  className,
  children,
  enableBottombar = true,
  enableTopbar = true,
  renderGreeting = false,
}) => {
  const {
    user: { admissionId },
  } = useAuth();

  const { data } = useQuery<QueryResult>(SELECT_QUERY, {
    variables: {
      admissionId,
    },
    skip: !renderGreeting,
    fetchPolicy: 'network-only',
  });

  return (
    <div className={className}>
      {enableTopbar && (
        <Topbar
          patientName={data?.admission.patient.name || undefined}
          renderGreeting={renderGreeting}
        />
      )}

      <main
        className={classNames(styles.main, {
          'has-greeting': renderGreeting,
        })}
      >
        {children ?? <Outlet />}
      </main>

      {enableBottombar && <BottomNavbar />}
    </div>
  );
};

export default NavbarTemplate;
